import React, { useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { normalizeAssetUrl } from '../../../utils/helper/assetUrl';

const getSignatureImageSrc = (signature) => {
    if (!signature || typeof signature !== 'object') {
        return '';
    }

    const rawSrc = (
        signature.image_url
        || signature.imageUrl
        || signature.url
        || signature.path
        || ''
    );

    return normalizeAssetUrl(rawSrc);
};

export const HandoverSignaturePreview = ({ signature, title = 'Signature', className = '' }) => {
    const imageSrc = useMemo(() => getSignatureImageSrc(signature), [signature]);
    const normalizedTitle = String(title || '').trim().toLowerCase();
    const normalizedUserName = String(signature?.user_name || '').trim().toLowerCase();
    const shouldShowUserName = Boolean(normalizedUserName) && normalizedUserName !== normalizedTitle;

    if (!imageSrc) {
        return null;
    }

    return (
        <div className={`rounded-xl border border-slate-200 bg-white p-3 ${className}`.trim()}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
            {shouldShowUserName ? (
                <p className="mt-1 text-xs text-slate-600">{signature.user_name}</p>
            ) : null}
            <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white">
                <img src={imageSrc} alt={title} className="h-28 w-full object-contain bg-white" />
            </div>
        </div>
    );
};

export const HandoverSignatureViewerModal = ({
    isOpen,
    signatures = [],
    title = 'Signatures',
    onClose,
}) => {
    const visibleSignatures = useMemo(
        () => (Array.isArray(signatures) ? signatures.filter((item) => getSignatureImageSrc(item?.signature)) : []),
        [signatures]
    );

    if (!isOpen || visibleSignatures.length === 0) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.35)]">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                        Close
                    </button>
                </div>

                <div className="bg-slate-50 p-5">
                    <div className={`grid gap-4 ${visibleSignatures.length > 1 ? 'md:grid-cols-2' : ''}`.trim()}>
                        {visibleSignatures.map((item) => (
                            <HandoverSignaturePreview
                                key={item.key}
                                signature={item.signature}
                                title={item.title}
                                className="h-full"
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const HandoverSignaturePad = ({
    title = '',
    label = '',
    description = '',
    placeholder = '',
    clearLabel = 'Clear',
    value = '',
    onChange,
    className = '',
    headerClassName = '',
    canvasWrapClassName = '',
    canvasClassName = '',
    showHeader = true,
}) => {
    const sigRef = useRef(null);
    const lastAppliedValueRef = useRef('');
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
        if (!sigRef.current) {
            return;
        }

        if (!value) {
            if (!sigRef.current.isEmpty()) {
                sigRef.current.clear();
            }
            lastAppliedValueRef.current = '';
            setIsEmpty(true);
            return;
        }

        if (value === lastAppliedValueRef.current || !sigRef.current.isEmpty()) {
            return;
        }

        try {
            sigRef.current.fromDataURL(value);
            lastAppliedValueRef.current = value;
            setIsEmpty(false);
        } catch (_) {}
    }, [value]);

    const handleClear = () => {
        sigRef.current?.clear();
        lastAppliedValueRef.current = '';
        setIsEmpty(true);
        onChange?.('');
    };

    const handleEnd = () => {
        const empty = sigRef.current?.isEmpty();
        const nextValue = empty ? '' : sigRef.current?.toDataURL();
        lastAppliedValueRef.current = nextValue || '';
        setIsEmpty(Boolean(empty));
        onChange?.(nextValue || '');
    };

    return (
        <div className={`rounded-xl border border-slate-200 bg-white p-3 ${className}`.trim()}>
            {showHeader ? (
                <div className={`flex items-start justify-between gap-3 ${headerClassName}`.trim()}>
                    <div>
                        {title ? (
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
                        ) : null}
                        {label ? (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{label}</p>
                        ) : null}
                        {description ? (
                            <p className={`${title || label ? 'mt-1' : ''} text-sm text-slate-600`.trim()}>{description}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 transition hover:text-slate-600"
                    >
                        {clearLabel}
                    </button>
                </div>
            ) : null}

            <div className={`mt-3 rounded-xl  ${canvasWrapClassName}`.trim()}>
                <div className="relative">
                    <SignatureCanvas
                        ref={sigRef}
                        penColor="black"
                        clearOnResize={false}
                        canvasProps={{
                            className: `w-full h-[200px] rounded-lg bg-white ${canvasClassName}`.trim(),
                        }}
                        onEnd={handleEnd}
                    />
                    {placeholder && isEmpty ? (
                        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                            <div className="w-[78%] border-t border-slate-200" />
                            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-300">
                                {placeholder}
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export const HandoverSignatureModal = ({
    isOpen,
    title,
    description,
    confirmLabel = 'Save Signature',
    cancelLabel = 'Cancel',
    confirmButtonClassName = 'bg-green-600 hover:bg-green-700',
    onCancel,
    onConfirm,
    loading = false,
}) => {
    const sigRef = useRef(null);
    const [signatureValue, setSignatureValue] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setSignatureValue('');
            sigRef.current?.clear();
        }
    }, [isOpen]);

    const handleClear = () => {
        sigRef.current?.clear();
        setSignatureValue('');
    };

    const handleEnd = () => {
        const nextValue = sigRef.current?.isEmpty() ? '' : sigRef.current?.toDataURL();
        setSignatureValue(nextValue || '');
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-950/60 p-4">
            <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.35)]">
                <div className="border-b border-slate-200 px-5 py-4">
                    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                    {description ? (
                        <p className="mt-1 text-sm text-slate-600">{description}</p>
                    ) : null}
                </div>

                <div className="px-5 py-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <SignatureCanvas
                            ref={sigRef}
                            penColor="black"
                            clearOnResize={false}
                            canvasProps={{
                                className: 'w-full h-[200px] rounded-lg bg-white',
                            }}
                            onEnd={handleEnd}
                        />
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                onClick={handleClear}
                                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 px-5 py-4">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={() => signatureValue && !loading && onConfirm?.(signatureValue)}
                        disabled={!signatureValue || loading}
                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmButtonClassName}`.trim()}
                    >
                        {loading ? 'Saving...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};
